import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Search, Package, ShoppingBag, Loader2, Edit, Trash2, Eye, EyeOff,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { ImageWithFallback } from '../ui/image-with-fallback';
import { DualPrice } from '../DualPrice';

/**
 * Searchable + paginated grid of the merchant's own services. Pure
 * presentational — all state, paging, and mutation handlers come from the
 * parent.
 */
export function ServicesList({
  services, servicesLoading, search, onSearchChange,
  page, totalPages, onPageChange,
  onToggleActive, onEdit, onDelete,
}) {
  return (
    <>
      <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
        <h2 className="text-white font-outfit font-semibold text-lg">Your Service Listings</h2>
        <div className="relative max-w-xs w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C7599]" />
          <Input
            placeholder="Search services..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 bg-[#041A3B] border-white/10 text-white"
            data-testid="merchant-service-search"
          />
        </div>
      </div>

      {servicesLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" /></div>
      ) : services.length === 0 ? (
        <div className="text-center py-16">
          <Package className="w-12 h-12 text-[#5C7599]/30 mx-auto mb-3" />
          <p className="text-[#5C7599]">No services yet. Click &quot;Add Service&quot; to create your first listing.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {services.map((svc) => (
            <Card key={svc.id} className="bg-[#041A3B] border-white/10" data-testid={`service-card-${svc.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-14 h-14 rounded-lg bg-[#000B18] flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {svc.image_url ? (
                    <ImageWithFallback
                      src={svc.image_url}
                      alt={svc.name || 'Service image'}
                      className="w-full h-full object-cover"
                      fallback={<ShoppingBag className="w-6 h-6 text-[#5C7599]" />}
                    />
                  ) : (
                    <ShoppingBag className="w-6 h-6 text-[#5C7599]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-medium text-sm truncate">{svc.name}</h3>
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${svc.is_active ? 'bg-[#00E676]/10 text-[#00E676]' : 'bg-white/5 text-[#5C7599]'}`}>
                      {svc.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-[#5C7599] text-xs truncate mt-0.5">{svc.description}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[#A0B4CC] text-xs">
                      <DualPrice php={svc.regular_price} phpDecimals={2} usdClassName="opacity-70 ml-1" />
                    </span>
                    {svc.tier_prices && Object.keys(svc.tier_prices).length > 0 && (
                      <span className="text-[#00E676] text-[10px] font-medium">
                        {Object.keys(svc.tier_prices).length} tier{Object.keys(svc.tier_prices).length > 1 ? 's' : ''} configured
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => onToggleActive(svc)} className="p-2 rounded-lg hover:bg-white/5 transition-colors" title={svc.is_active ? 'Deactivate' : 'Activate'} data-testid={`toggle-service-${svc.id}`}>
                    {svc.is_active ? <Eye className="w-4 h-4 text-[#00E676]" /> : <EyeOff className="w-4 h-4 text-[#5C7599]" />}
                  </button>
                  <button onClick={() => onEdit(svc)} className="p-2 rounded-lg hover:bg-white/5 transition-colors" data-testid={`edit-service-${svc.id}`}>
                    <Edit className="w-4 h-4 text-[#00E5FF]" />
                  </button>
                  <button onClick={() => onDelete(svc.id)} className="p-2 rounded-lg hover:bg-[#FF1744]/10 transition-colors" data-testid={`delete-service-${svc.id}`}>
                    <Trash2 className="w-4 h-4 text-[#FF1744]" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-[#5C7599] text-xs">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => onPageChange(page - 1)} className="text-[#A0B4CC]">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} className="text-[#A0B4CC]">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
